import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import type { AuditEntryDto } from '@tww/shared';
import { fmtDate, fmtDateTime } from '@tww/shared';
import { api, qs } from '../../api/client';
import { ErrorState, Panel, Skeleton } from '../../ui/basics';
import { Select, TextInput } from '../../ui/fields';
import { settingLabel } from './settingDefs';

const ACTION: Record<string, string> = { create: 'angelegt', update: 'geändert', archive: 'archiviert', restore: 'wiederhergestellt', login: 'Anmeldung', logout: 'Abmeldung', login_failed: 'Anmeldung fehlgeschlagen', role_change: 'Rolle geändert', permission_change: 'Rechte geändert', export: 'Export', email_sent: 'E-Mail gesendet', document_generated: 'Dokument erzeugt', import: 'Import', merge: 'zusammengeführt' };

/** Datensatzarten mit deutschem Namen und, wo es eine Akte gibt, Link */
const ENTITY: Record<string, { label: string; href?: (id: number) => string }> = {
  contact: { label: 'Kontakt', href: (id) => `/kontakte/${id}` },
  action: { label: 'Aktion', href: (id) => `/aktionen/${id}` },
  area: { label: 'Fläche', href: (id) => `/flaechen/${id}` },
  monitoring: { label: 'Monitoring', href: (id) => `/monitoring/${id}` },
  site_visit: { label: 'Begehungsprotokoll', href: (id) => `/protokoll/${id}` },
  document: { label: 'Dokument' },
  document_folder: { label: 'Ordner' },
  email: { label: 'E-Mail' },
  user: { label: 'Nutzer' },
  permissions: { label: 'Rechte' },
  setting: { label: 'Einstellung' },
  contact_category: { label: 'Kontaktkategorie' },
  lookup_list: { label: 'Stammliste' },
  lookup_value: { label: 'Stammlisten-Wert' },
  checklist_template: { label: 'Checklisten-Vorlage' },
  checklist_group: { label: 'Checklisten-Gewerk' },
  checklist_item: { label: 'Checklistenpunkt' },
  document_template: { label: 'Dokumentvorlage' },
  email_template: { label: 'E-Mail-Vorlage' },
  import_batch: { label: 'Import' },
  database: { label: 'Datenbank' },
};

/** Gängige Feldnamen; alles andere wird mit Leerzeichen statt Unterstrichen angezeigt */
const FIELD: Record<string, string> = {
  name: 'Name', display_name: 'Name', first_name: 'Vorname', last_name: 'Nachname', org_name: 'Organisation', title: 'Titel', salutation: 'Anrede', position: 'Position',
  email: 'E-Mail', phone: 'Telefon', mobile: 'Mobil', website: 'Website', street: 'Straße', zip: 'PLZ', city: 'Ort', state_code: 'Bundesland', region: 'Region', notes: 'Notizen', kind: 'Art',
  categories: 'Kategorien', rating_grade: 'Bewertung', rating_reason: 'Begründung', status: 'Status', date: 'Datum', code: 'Kennung', label: 'Anzeige', is_active: 'aktiv', active: 'aktiv',
  roles: 'Rollen', role: 'Rolle', initials: 'Kürzel', daily_digest: 'Tagesmail', type_code: 'Aktionstyp', season_code: 'Saison', year: 'Jahr', price_per_tree: 'Baumpreis',
  partner_contact_id: 'Spendenpartner', forester_contact_id: 'Förster', area_id: 'Fläche', action_id: 'Aktion', municipality: 'Gemeinde', subject: 'Betreff', body: 'Text', file: 'Datei',
  version: 'Version', archived_at: 'archiviert', sort: 'Reihenfolge', color: 'Farbe', meta: 'Zusatz', fields: 'Felder', name_plural: 'Mehrzahl', kinds_allowed: 'Erlaubt für',
  rating_enabled: 'Bewertung möglich', key: 'Schlüssel', list: 'Liste', deactivated: 'deaktiviert', deleted: 'gelöscht', contacts: 'Kontakte', path: 'Pfad', from: 'von', to: 'nach',
  template: 'Vorlage', due_date: 'Frist', done: 'erledigt', values: 'Werte', size_ha: 'Größe (ha)', trees: 'Bäume', tree_count: 'Bäume', participants: 'Teilnehmende',
};
const fieldName = (k: string) => FIELD[k] ?? k.replace(/_/g, ' ');

/** Wert ohne JSON-Anführungszeichen, Datumswerte deutsch */
function fmtValue(v: unknown): string {
  if (v == null || v === '') return '–';
  if (v === true) return 'ja';
  if (v === false) return 'nein';
  if (typeof v === 'number') return String(v).replace('.', ',');
  if (typeof v === 'string') {
    if (/^\d{4}-\d{2}-\d{2}T/.test(v)) return fmtDateTime(v);
    if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return fmtDate(v);
    return v.length > 120 ? `${v.slice(0, 120)} …` : v;
  }
  if (Array.isArray(v)) return v.length ? v.map((x) => (x && typeof x === 'object' ? ((x as Record<string, unknown>).label ?? (x as Record<string, unknown>).name ?? '…') : fmtValue(x))).join(', ') : '–';
  const o = v as Record<string, unknown>;
  return Object.entries(o)
    .map(([k, x]) => `${fieldName(k)}: ${fmtValue(x)}`)
    .join(', ');
}
const isChange = (v: unknown): v is { old: unknown; new: unknown } => typeof v === 'object' && v != null && 'old' in v && 'new' in v;

function changesText(a: AuditEntryDto): string {
  const c = a.changes;
  if (!c) return '';
  // Einstellungen: { key, old, new }
  if (a.entity_type === 'setting' && typeof c.key === 'string' && 'new' in c) return `${settingLabel(c.key)}: ${fmtValue(c.old)} → ${fmtValue(c.new)}`;
  return Object.entries(c)
    .map(([k, v]) => (isChange(v) ? `${fieldName(k)}: ${fmtValue(v.old)} → ${fmtValue(v.new)}` : `${fieldName(k)}: ${fmtValue(v)}`))
    .join(' · ');
}

/** Änderungsprotokoll über alle Datensätze */
export function AuditPage() {
  const [type, setType] = useState('');
  const [id, setId] = useState('');
  const q = useQuery({ queryKey: ['audit', type, id], queryFn: () => api.get<{ items: AuditEntryDto[] }>(`/audit${qs({ entity_type: type, entity_id: /^\d+$/.test(id) ? id : undefined, limit: 200 })}`).then((r) => r.items) });
  const typeOptions = Object.entries(ENTITY)
    .map(([value, e]) => ({ value, label: e.label }))
    .sort((a, b) => a.label.localeCompare(b.label, 'de'));
  return (
    <>
      <div className="filterbar">
        <Select label="Datensatzart" value={type} onChange={setType} options={typeOptions} placeholder="alle" />
        <TextInput label="Nr." value={id} onChange={setId} placeholder="alle" />
      </div>
      <Panel title="Letzte Einträge">
        {q.isLoading ? (
          <Skeleton />
        ) : q.isError ? (
          <ErrorState error={q.error} retry={() => q.refetch()} />
        ) : q.data!.length === 0 ? (
          <div className="empty">Keine Einträge</div>
        ) : (
          <div className="tw">
            <table>
              <thead>
                <tr>
                  <th>Zeit</th>
                  <th>Nutzer</th>
                  <th>Aktion</th>
                  <th>Datensatz</th>
                  <th>Änderungen</th>
                </tr>
              </thead>
              <tbody>
                {q.data!.map((a) => {
                  const e = ENTITY[a.entity_type];
                  const label = `${e?.label ?? fieldName(a.entity_type)}${a.entity_id != null ? ` ${a.entity_id}` : ''}`;
                  return (
                    <tr key={a.id}>
                      <td className="nowrap">{fmtDateTime(a.at)}</td>
                      <td>{a.user_name ?? <span className="mut">System</span>}</td>
                      <td>{ACTION[a.action] ?? a.action}</td>
                      <td className="nowrap">
                        {e?.href && a.entity_id != null ? (
                          <Link className="link" to={e.href(a.entity_id)}>
                            {label}
                          </Link>
                        ) : (
                          label
                        )}
                      </td>
                      <td className="small audit-changes">{changesText(a)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </>
  );
}
